import React, { useState } from "react";
import { styled } from "@material-ui/styles";
import { IStyledArguments } from "../../types/styled-arguments";
import {
  Box,
  Card,
  Grid,
  TextField,
  InputAdornment,
  Chip,
  Avatar,
  Typography as MuiTypography,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
  useTheme,
  Theme,
} from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import PersonIcon from "@material-ui/icons/Person";
import SportsIcon from "@material-ui/icons/Sports";
import EmailIcon from "@material-ui/icons/Email";
import PhoneIcon from "@material-ui/icons/Phone";
import WorkIcon from "@material-ui/icons/Work";
import EditIcon from "@material-ui/icons/Edit";
import MoreVertIcon from "@material-ui/icons/MoreVert";
import LinkIcon from "@material-ui/icons/Link";
import LinkOffIcon from "@material-ui/icons/LinkOff";
import { IWorkspaceUser, ICoachUser } from "../../types";
import { useMutation } from "@apollo/client";
import { CHANGE_USER_ROLE } from "./WorkspaceManagementQueries";

// Styled Components
const Container = styled(Box)(({ theme }: IStyledArguments) => ({
  padding: theme.spacing(3),
}));

const SearchBar = styled(TextField)(({ theme }: IStyledArguments) => ({
  marginBottom: theme.spacing(3),
  "& .MuiOutlinedInput-root": {
    borderRadius: "12px",
    background: "white",
  },
}));

const FilterChipsContainer = styled(Box)(({ theme }: IStyledArguments) => ({
  display: "flex",
  gap: theme.spacing(1),
  marginBottom: theme.spacing(3),
  flexWrap: "wrap",
}));

const FilterChip = styled(Chip)(() => ({
  borderRadius: "8px",
  fontWeight: 600,
  cursor: "pointer",
  transition: "all 0.2s ease",
}));

const UserCard = styled(Card)(({ theme }: IStyledArguments) => ({
  borderRadius: "16px",
  padding: theme.spacing(3),
  height: "100%",
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(2),
  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08)",
  transition: "all 0.2s ease",
  "&:hover": {
    transform: "translateY(-4px)",
    boxShadow: "0 8px 24px rgba(0, 0, 0, 0.12)",
  },
}));

const UserHeader = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: "16px",
});

const UserAvatar = styled(Avatar)(({ theme }: IStyledArguments) => ({
  width: 60,
  height: 60,
  background: theme.palette.primary.main,
}));

const UserInfo = styled(Box)({
  flex: 1,
});

const StatusBadge = styled(Chip)(() => ({
  borderRadius: "6px",
  fontWeight: 600,
  fontSize: "0.75rem",
  height: "24px",
}));

const DetailRow = styled(Box)(({ theme }: IStyledArguments) => ({
  display: "flex",
  alignItems: "center",
  gap: "8px",
  color: theme.palette.text.secondary,
  fontSize: "0.875rem",
}));

const RoleChip = styled(Chip)(({ theme }: IStyledArguments) => ({
  borderRadius: "6px",
  height: "28px",
  fontWeight: 500,
  background: theme.palette.grey[100],
}));

const EmptyState = styled(Box)(({ theme }: IStyledArguments) => ({
  textAlign: "center",
  padding: theme.spacing(8, 2),
  color: theme.palette.text.secondary,
}));

const EmptyIcon = styled(PersonIcon)(({ theme }: IStyledArguments) => ({
  fontSize: "4rem",
  color: theme.palette.grey[300],
  marginBottom: theme.spacing(2),
}));

const StatsCard = styled(Card)(({ theme }: IStyledArguments) => ({
  borderRadius: "12px",
  padding: theme.spacing(2),
  background: theme.palette.primary.main,
  color: "white",
  marginBottom: theme.spacing(3),
}));

const StatsGrid = styled(Grid)({
  "& .MuiGrid-item": {
    textAlign: "center",
  },
});

interface IUsersManagementProps {
  users: IWorkspaceUser[];
  coaches: ICoachUser[];
  workspaceId: string;
  onRefresh: () => void;
}

type FilterType = "all" | "with-links" | "without-links" | "coaches";

// Helper function to get status badge style
const getStatusBadgeStyle = (status: "regular" | "coach") => {
  if (status === "regular") {
    return {
      backgroundColor: "#E3F1F0",
      color: "#0E7C86",
    };
  }
  if (status === "coach") {
    return {
      backgroundColor: "#fce4ec",
      color: "#c2185b",
    };
  }
  return {};
};

const UsersManagement: React.FC<IUsersManagementProps> = ({
  users,
  coaches,
  onRefresh,
}) => {
  const theme = useTheme<Theme>();
  const [searchTerm, setSearchTerm] = useState("");
  const [activeFilter, setActiveFilter] = useState<FilterType>("all");
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedUser, setSelectedUser] = useState<IWorkspaceUser | null>(null);

  const [changeUserRole, { loading: changeRoleLoading }] = useMutation(CHANGE_USER_ROLE, {
    onCompleted: (data) => {
      if (data.changeUserRole.success) {
        onRefresh();
        handleCloseMenu();
        alert(data.changeUserRole.message || "Role changed successfully");
      }
    },
    onError: (error) => {
      alert(`Error changing role: ${error.message}`);
    },
  });

  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>, user: IWorkspaceUser) => {
    setAnchorEl(event.currentTarget);
    setSelectedUser(user);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setSelectedUser(null);
  };

  const handleChangeRole = (newRole: string) => {
    if (selectedUser) {
      changeUserRole({
        variables: {
          userId: selectedUser.id,
          newRole: newRole,
        },
      });
    }
  };

  // Calcular estadísticas
  // NOTE: Con N:N, mostramos cuántos usuarios tienen al menos 1 vinculación
  const coachUsers = users.filter((u) => u.role === "coach");
  const usersWithLinks = users.filter(
    (u) => u.linkedPlayers && u.linkedPlayers.length > 0
  );
  const usersWithoutLinks = users.filter(
    (u) => !u.linkedPlayers || u.linkedPlayers.length === 0
  );

  // Función para determinar el estado de un usuario
  const getUserStatus = (user: IWorkspaceUser): "coach" | "regular" => {
    if (user.role === "coach") return "coach";
    return "regular";
  };

  // Función para obtener la etiqueta de estado
  const getStatusLabel = (status: "coach" | "regular"): string => {
    switch (status) {
      case "regular":
        return "User";
      case "coach":
        return "Coach";
    }
  };

  // Filtrar usuarios según búsqueda y filtro activo
  const filteredUsers = users.filter((user) => {
    // Filtro de búsqueda
    const matchesSearch =
      user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.surname.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase());

    if (!matchesSearch) return false;

    // Filtro por tipo
    const status = getUserStatus(user);
    const hasLinks = user.linkedPlayers && user.linkedPlayers.length > 0;
    
    if (activeFilter === "all") return true;
    if (activeFilter === "coaches") return status === "coach";
    if (activeFilter === "with-links") return hasLinks;
    if (activeFilter === "without-links") return !hasLinks;

    return true;
  });

  // Encontrar detalles del coach si el usuario es coach
  const getCoachDetails = (userId: string) => {
    return coaches.find((c) => c.id === userId);
  };

  return (
    <Container>
      {/* Estadísticas */}
      <StatsCard>
        <MuiTypography variant="h6" gutterBottom style={{ fontWeight: 600 }}>
          User Statistics
        </MuiTypography>
        <StatsGrid container spacing={2}>
          <Grid item xs={3}>
            <MuiTypography variant="h3" style={{ fontWeight: 700 }}>
              {users.length}
            </MuiTypography>
            <MuiTypography variant="body2">Total Users</MuiTypography>
          </Grid>
          <Grid item xs={3}>
            <MuiTypography variant="h3" style={{ fontWeight: 700, color: theme.palette.primary.main }}>
              {usersWithLinks.length}
            </MuiTypography>
            <MuiTypography variant="body2">With Links</MuiTypography>
          </Grid>
          <Grid item xs={3}>
            <MuiTypography variant="h3" style={{ fontWeight: 700, color: "#757575" }}>
              {usersWithoutLinks.length}
            </MuiTypography>
            <MuiTypography variant="body2">Without Links</MuiTypography>
          </Grid>
          <Grid item xs={3}>
            <MuiTypography variant="h3" style={{ fontWeight: 700, color: "#c2185b" }}>
              {coachUsers.length}
            </MuiTypography>
            <MuiTypography variant="body2">Coaches</MuiTypography>
          </Grid>
        </StatsGrid>
      </StatsCard>

      {/* Barra de búsqueda */}
      <SearchBar
        fullWidth
        variant="outlined"
        placeholder="Search users by name or email..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
        }}
      />

      {/* Filtros */}
      <FilterChipsContainer>
        <FilterChip
          label={`All Users (${users.length})`}
          icon={<PersonIcon />}
          onClick={() => setActiveFilter("all")}
          color={activeFilter === "all" ? "primary" : "default"}
        />
        <FilterChip
          label={`With Links (${usersWithLinks.length})`}
          icon={<LinkIcon />}
          onClick={() => setActiveFilter("with-links")}
          color={activeFilter === "with-links" ? "primary" : "default"}
        />
        <FilterChip
          label={`Without Links (${usersWithoutLinks.length})`}
          icon={<LinkOffIcon />}
          onClick={() => setActiveFilter("without-links")}
          color={activeFilter === "without-links" ? "primary" : "default"}
        />
        <FilterChip
          label={`Coaches (${coachUsers.length})`}
          icon={<SportsIcon />}
          onClick={() => setActiveFilter("coaches")}
          color={activeFilter === "coaches" ? "primary" : "default"}
        />
      </FilterChipsContainer>

      {/* Lista de usuarios */}
      {filteredUsers.length === 0 ? (
        <EmptyState>
          <EmptyIcon />
          <MuiTypography variant="h6" gutterBottom>
            No users found
          </MuiTypography>
          <MuiTypography variant="body2" color="textSecondary">
            {searchTerm
              ? "Try adjusting your search or filters"
              : "No users match the selected filter"}
          </MuiTypography>
        </EmptyState>
      ) : (
        <Grid container spacing={3}>
          {filteredUsers.map((user) => {
            const status = getUserStatus(user);
            const coachDetails = status === "coach" ? getCoachDetails(user.id) : null;

            return (
              <Grid item xs={12} sm={6} md={4} key={user.id}>
                <UserCard>
                  {/* Header */}
                  <UserHeader>
                    <UserAvatar src={user.avatarUrl || undefined}>
                      {!user.avatarUrl && `${user.name[0]}${user.surname[0]}`}
                    </UserAvatar>
                    <UserInfo>
                      <MuiTypography variant="h6" style={{ fontWeight: 600, margin: 0, marginBottom: 4 }}>
                        {user.name} {user.surname}
                      </MuiTypography>
                      <StatusBadge
                        label={getStatusLabel(status)}
                        size="small"
                        style={getStatusBadgeStyle(status)}
                      />
                    </UserInfo>
                    <Tooltip title="Change role">
                      <IconButton
                        size="small"
                        onClick={(e) => handleOpenMenu(e, user)}
                        disabled={changeRoleLoading}
                      >
                        <MoreVertIcon />
                      </IconButton>
                    </Tooltip>
                  </UserHeader>

                  {/* Información básica */}
                  <Box display="flex" flexDirection="column" style={{ gap: "12px" }}>
                    <DetailRow>
                      <EmailIcon fontSize="small" />
                      <MuiTypography variant="body2">{user.email}</MuiTypography>
                    </DetailRow>

                    <DetailRow>
                      <WorkIcon fontSize="small" />
                      <RoleChip label={user.role} size="small" />
                    </DetailRow>

                    {/* Información adicional para coaches */}
                    {status === "coach" && coachDetails && (
                      <>
                        {coachDetails.specialty && (
                          <DetailRow>
                            <SportsIcon fontSize="small" />
                            <MuiTypography variant="body2">
                              {coachDetails.specialty}
                            </MuiTypography>
                          </DetailRow>
                        )}
                        {coachDetails.telephone && (
                          <DetailRow>
                            <PhoneIcon fontSize="small" />
                            <MuiTypography variant="body2">
                              {coachDetails.telephone}
                            </MuiTypography>
                          </DetailRow>
                        )}
                        {coachDetails.teams && coachDetails.teams.length > 0 && (
                          <Box>
                            <MuiTypography
                              variant="caption"
                              color="textSecondary"
                              display="block"
                              gutterBottom
                            >
                              Teams
                            </MuiTypography>
                            <Box display="flex" flexWrap="wrap" style={{ gap: "4px" }}>
                              {coachDetails.teams.map((team) => (
                                <Chip
                                  key={team.id}
                                  label={team.name}
                                  size="small"
                                  style={{ height: "24px" }}
                                />
                              ))}
                            </Box>
                          </Box>
                        )}
                      </>
                    )}

                    {/* Linked Players - N:N relationship */}
                    {user.linkedPlayers && user.linkedPlayers.length > 0 && (
                      <Box mt={2} pt={2} borderTop="1px solid #eee">
                        <MuiTypography
                          variant="caption"
                          color="textSecondary"
                          display="block"
                          gutterBottom
                          style={{ fontWeight: 600 }}
                        >
                          Linked to Players ({user.linkedPlayers.length})
                        </MuiTypography>
                        <Box display="flex" flexWrap="wrap" style={{ gap: "6px" }}>
                          {user.linkedPlayers.map((player) => (
                            <Chip
                              key={player.id}
                              label={`${player.name} ${player.surname}${
                                player.number ? ` #${player.number}` : ""
                              }`}
                              size="small"
                              color="primary"
                              variant="outlined"
                              style={{
                                height: "26px",
                                fontSize: "0.75rem",
                                borderColor: theme.palette.primary.main,
                              }}
                            />
                          ))}
                        </Box>
                      </Box>
                    )}
                  </Box>

                  {/* Fecha de creación */}
                  {user.createdAt && (
                    <Box borderTop="1px solid #eee" pt={1.5} mt="auto">
                      <MuiTypography variant="caption" color="textSecondary">
                        Created: {new Date(user.createdAt).toLocaleDateString()}
                      </MuiTypography>
                    </Box>
                  )}
                </UserCard>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* Role Change Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleCloseMenu}
        keepMounted
      >
        <MenuItem disabled style={{ fontWeight: 600, color: "#333" }}>
          <EditIcon fontSize="small" style={{ marginRight: 8 }} />
          Change Role for {selectedUser?.name}
        </MenuItem>
        <MenuItem onClick={() => handleChangeRole("Manager")}>
          Manager
        </MenuItem>
        <MenuItem onClick={() => handleChangeRole("coach")}>
          Coach
        </MenuItem>
        <MenuItem onClick={() => handleChangeRole("parent")}>
          Parent
        </MenuItem>
        <MenuItem onClick={() => handleChangeRole("player")}>
          Player
        </MenuItem>
        <MenuItem onClick={() => handleChangeRole("Admin")}>
          Admin
        </MenuItem>
      </Menu>
    </Container>
  );
};

export default UsersManagement;
